import { useCallback, useRef, useState } from 'react'
import { produce } from 'immer'
import { useLocation, useNavigate, useSearchParams } from 'react-router'
import toString from 'lodash/toString'
import { ROUTE_RECEIVABLE, ROUTE_RECEIVABLES, buildNavPathTrader } from '@klear/constants'
import { STEP_ID, STEP_ORDER, type StepId } from '@/app/receivable/workflow/receivable-steps'
import { positiveIntFromQuery, stepIdFromHash } from '@/app/workflow/wizard-url-seed'

export type ReceivableForm = {
  orderId: number | null
  purchaseOrderEventId: number | null
  invoiceEventId: number | null
}

export function isReceivableHostStepSatisfied(stepId: StepId, form: ReceivableForm): boolean {
  switch (stepId) {
    case STEP_ID.SELECT_ORDER:
      return form.orderId != null && form.orderId > 0
    case STEP_ID.PURCHASE_ORDER:
      return form.purchaseOrderEventId != null && form.purchaseOrderEventId > 0
    case STEP_ID.INVOICE:
      return form.invoiceEventId != null && form.invoiceEventId > 0
    case STEP_ID.PROOF_OF_ACCEPTANCE:
      return true
    case STEP_ID.REVIEW:
      return false
    default:
      return false
  }
}

export function initialReceivableStepId(form: ReceivableForm, hashStep?: StepId | null): StepId {
  if (hashStep != null) {
    const idx = STEP_ORDER.indexOf(hashStep)
    if (idx >= 0) {
      const priorOk = STEP_ORDER.slice(0, idx).every((s) => isReceivableHostStepSatisfied(s, form))
      if (priorOk) return hashStep
    }
  }
  if (form.purchaseOrderEventId != null && form.purchaseOrderEventId > 0) return STEP_ID.INVOICE
  if (form.orderId != null && form.orderId > 0) return STEP_ID.PURCHASE_ORDER
  return STEP_ID.SELECT_ORDER
}

export function useReceivableWizard(opts: { traderIdParam: string; orderIdSeed?: number | null }) {
  const { traderIdParam, orderIdSeed } = opts
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const purchaseOrderIdSeed = positiveIntFromQuery(searchParams.get('purchaseOrderId'))
  const hashStepSeed = stepIdFromHash(location.hash, STEP_ORDER)

  const [form, setForm] = useState<ReceivableForm>(() => ({
    orderId: orderIdSeed ?? null,
    purchaseOrderEventId: purchaseOrderIdSeed,
    invoiceEventId: null
  }))
  const [proofAdded, setProofAdded] = useState(false)
  const formRef = useRef(form)
  formRef.current = form

  const getForm = useCallback(() => formRef.current, [])

  const patchForm = useCallback((recipe: (draft: ReceivableForm) => void) => {
    const next = produce(formRef.current, recipe)
    formRef.current = next
    setForm(next)
  }, [])

  const attachExistingPurchaseOrder = useCallback((purchaseOrderEventId: number) => {
    const next = produce(formRef.current, (draft) => {
      draft.purchaseOrderEventId = purchaseOrderEventId
    })
    formRef.current = next
    setForm(next)
  }, [])

  const markProofAdded = useCallback(() => {
    setProofAdded(true)
  }, [])

  const navigateToReceivables = useCallback(() => {
    if (traderIdParam) navigate(buildNavPathTrader(traderIdParam, ROUTE_RECEIVABLES))
  }, [navigate, traderIdParam])

  const locationRef = useRef(location)
  locationRef.current = location

  const syncUrl = useCallback(
    (step: string) => {
      if (!traderIdParam) return
      let path = buildNavPathTrader(traderIdParam, ROUTE_RECEIVABLE)
      if (form.orderId != null && form.orderId > 0) {
        path += `/order/${form.orderId}`
      }
      const params = new URLSearchParams()
      if (form.purchaseOrderEventId != null && form.purchaseOrderEventId > 0) {
        params.set('purchaseOrderId', toString(form.purchaseOrderEventId))
      }
      const qs = params.toString()
      const query = qs ? `?${qs}` : ''
      const hash = step !== STEP_ID.SELECT_ORDER ? `#${encodeURIComponent(step)}` : ''
      const next = `${path}${query}${hash}`
      const loc = locationRef.current
      const current = `${loc.pathname}${loc.search}${loc.hash}`
      if (next === current) return
      navigate(next, { replace: true })
    },
    [form.orderId, form.purchaseOrderEventId, navigate, traderIdParam]
  )

  return {
    form,
    getForm,
    patchForm,
    proofAdded,
    attachExistingPurchaseOrder,
    markProofAdded,
    hashStepSeed,
    syncUrl,
    handleDone: navigateToReceivables
  }
}

export type ReceivableWizardApi = ReturnType<typeof useReceivableWizard>
